import { type CSSProperties } from "react";
import { useEmbedTokens } from "../theme";

interface Props {
  value: number;
  min?: number;
  max?: number;
  onIncrease: () => void;
  onDecrease: () => void;
  disabled?: boolean;
  ariaLabel?: string;
}

// Joined tray: [ − ][ value ][ + ] sharing one bordered pill. Compact (~32px) but
// ≥24px hit area (WCAG 2.5.8 AA). EmbedButton's fixed height+padding would re-flow
// the cart line row — this primitive exists to express the compact shape.
export function EmbedQtyStepper({ value, min = 0, max, onIncrease, onDecrease, disabled = false, ariaLabel }: Props) {
  const t = useEmbedTokens();
  const atMin = value <= min;
  const atMax = max != null && value >= max;
  const trayStyle: CSSProperties = {
    display: "inline-flex", alignItems: "center", borderRadius: t.borderRadiusControl,
    border: `1px solid ${t.colors.border}`, overflow: "hidden", background: t.colors.surface,
  };
  const btnBase: CSSProperties = {
    width: 32, height: 32, border: "none", background: "transparent",
    fontFamily: "inherit", fontSize: t.fontSize.md, color: t.colors.text, cursor: "pointer",
  };
  const btn = (off: boolean): CSSProperties => ({ ...btnBase, opacity: off ? 0.4 : 1, cursor: off ? "not-allowed" : "pointer" });
  const valStyle: CSSProperties = {
    minWidth: 32, textAlign: "center", fontSize: t.fontSize.sm, fontWeight: 500,
    color: t.colors.text, padding: `0 ${t.spacing.xs}px`,
    borderLeft: `1px solid ${t.colors.border}`, borderRight: `1px solid ${t.colors.border}`,
  };
  return (
    <div role="group" aria-label={ariaLabel} style={trayStyle}>
      <button type="button" aria-label="Decrease quantity" onClick={onDecrease} disabled={disabled || atMin} style={btn(disabled || atMin)}>−</button>
      <span aria-label={`Quantity: ${value}`} style={valStyle}>{value}</span>
      <button type="button" aria-label="Increase quantity" onClick={onIncrease} disabled={disabled || atMax} style={btn(disabled || atMax)}>+</button>
    </div>
  );
}
